'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Menu, Search, Zap, X, Sun, Moon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

interface AppTopbarProps {
  onOpenMobileSidebar: () => void;
}

interface QuickResult {
  id: string;
  type: 'track' | 'artist';
  label: string;
  sub: string;
  href: string;
}

export default function AppTopbar({ onOpenMobileSidebar }: AppTopbarProps) {
  const { user, supabase } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const router = useRouter();
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<QuickResult[]>([]);
  const [showDrop, setShowDrop] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!query.trim()) { setSuggestions([]); setShowDrop(false); return; }
    const timer = setTimeout(async () => {
      try {
        const term = `%${query.trim()}%`;
        const [tracksRes, artistsRes] = await Promise.all([
          supabase.from('tracks').select('id, title, genre').ilike('title', term).eq('is_published', true).limit(4),
          supabase.from('user_profiles').select('id, full_name, genre').ilike('full_name', term).eq('role', 'artist').limit(3),
        ]);
        const results: QuickResult[] = [
          ...(tracksRes.data || []).map((t: any) => ({ id: t.id, type: 'track' as const, label: t.title, sub: t.genre || 'Track', href: '/music-discovery-feed' })),
          ...(artistsRes.data || []).map((a: any) => ({ id: a.id, type: 'artist' as const, label: a.full_name, sub: a.genre || 'Artist', href: `/artist/${a.id}` })),
        ];
        setSuggestions(results);
        setShowDrop(results.length > 0);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node) && inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowDrop(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setShowDrop(false);
      router.push(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <header className="h-14 sm:h-16 flex items-center px-3 sm:px-4 lg:px-6 gap-2 sm:gap-3 border-b flex-shrink-0"
      style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
      <button
        onClick={onOpenMobileSidebar}
        className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
        aria-label="Open sidebar"
      >
        <Menu size={20} />
      </button>

      {/* Search */}
      <div className="flex-1 max-w-[160px] xs:max-w-xs sm:max-w-md relative">
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowDrop(true)}
              placeholder="Search..."
              className="w-full pl-9 pr-8 py-2 text-sm rounded-lg outline-none"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
            />
            {query && (
              <button type="button" onClick={() => { setQuery(''); setSuggestions([]); setShowDrop(false); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2">
                <X size={13} style={{ color: 'var(--muted-foreground)' }} />
              </button>
            )}
          </div>
        </form>

        {/* Dropdown suggestions */}
        {showDrop && (
          <div ref={dropRef} className="absolute top-full left-0 right-0 mt-1 rounded-xl shadow-xl z-50 overflow-hidden"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            {suggestions.map((s) => (
              <Link key={`${s.type}-${s.id}`} href={s.href}
                onClick={() => { setShowDrop(false); setQuery(''); }}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: s.type === 'artist' ? 'rgba(168,85,247,0.15)' : 'rgba(42,157,143,0.15)' }}>
                  {s.type === 'artist' ? <Zap size={13} style={{ color: 'var(--primary)' }} /> : <Search size={13} style={{ color: 'var(--secondary)' }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{s.label}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>{s.sub}</p>
                </div>
                <span className="text-xs capitalize px-1.5 py-0.5 rounded hidden sm:inline" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>{s.type}</span>
              </Link>
            ))}
            <Link href={`/search?q=${encodeURIComponent(query)}`}
              onClick={() => setShowDrop(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-xs font-medium border-t transition-colors hover:bg-muted"
              style={{ borderColor: 'var(--border)', color: 'var(--secondary)' }}>
              <Search size={12} />
              See all results for "{query}"
            </Link>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 sm:gap-2 ml-auto flex-shrink-0">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg hover:bg-muted transition-colors"
          aria-label="Toggle theme"
          style={{ color: 'var(--muted-foreground)' }}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* PULSE badge */}
        <Link href="/pulse" className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full badge-purple text-xs font-semibold">
          <Zap size={12} />
          PULSE Live
        </Link>

        {/* Avatar */}
        <Link href="/settings">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer"
            style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}>
            {initials}
          </div>
        </Link>
      </div>
    </header>
  );
}